import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Folder, Plus, Search, Edit, Trash2, Download, Eye } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import images from "../../../assets/images";
import { Dropdown } from "../../../components/dropdown/Dropdown";

export interface DesignFolder {
  _id?: string;
  id: string;
  folderName: string;
  description?: string;
  files?: DesignDocument[];
  totalsize?: string;
  createdAt?: number;
  updatedAt?: number;
  createdDate?: string;
  uploadedBy?: string;
  documentType?: "architectural_drawing" | "blueprint" | "permit" | "other" | "all";
}

export interface DesignDocument {
  _id?: string;
  id?: string;
  folderId?: string;
  documentName: string;
  documentType: "architectural_drawing" | "blueprint" | "permit" | "other";
  description?: string;
  currentVersion: number;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  uploadedBy?: string;
  uploadedAt: string;
  updatedAt?: string;
  region?: string;
  premises?: string;
  tags?: string[];
  status?: "active" | "archived" | "draft";
  versions?: DocumentVersion[];
  signedUrl?: string;
}

export interface DocumentVersion {
  version: number;
  fileUrl: string;
  fileName: string;
  uploadedBy?: string;
  uploadedAt: string;
  changeDescription?: string;
  fileSize?: number;
}

const DesignDocumentationIntegration: React.FC = () => {
  const [folders, setFolders] = useState<DesignFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] = useState<string>("ALL Premises");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [folderDescription, setFolderDescription] = useState("");
  const [editFolder, setEditFolder] = useState<DesignFolder | null>(null);
  const [resMessage, setResMessage] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchFolders();
  }, [searchQuery, selectedRegion, selectedPremises, documentTypeFilter]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const params: { [key: string]: any } = {};
      
      if (searchQuery.trim()) params.keyword = searchQuery;
      if (selectedRegion && selectedRegion !== "ALL Regions") params.region = selectedRegion;
      if (selectedPremises && selectedPremises !== "ALL Premises") params.premises = selectedPremises;
      if (documentTypeFilter && documentTypeFilter !== "all") params.documentType = documentTypeFilter;

      const response = await axiosInstance.get("/api/utility-systems/design-folders", { params });
      
      if (response.data.success && response.data.resCode === 200) {
        const folderData = response.data.message || response.data.data || [];
        // Ensure each folder has both _id and id for compatibility
        const foldersWithIds = Array.isArray(folderData) 
          ? folderData.map((folder: any) => ({
              ...folder,
              id: folder.id || folder._id,
              _id: folder._id || folder.id,
            }))
          : [];
        setFolders(foldersWithIds);
      } else {
        setFolders([]);
      }
    } catch (error: any) {
      console.error("Error fetching folders:", error);
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.resMessage || "Failed to load folders");
      }
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (sizeInKB: number): string => {
    if (sizeInKB === 0) return "0 MB";
    const sizeInMB = sizeInKB / 1024;
    if (sizeInMB < 1024) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInGB = sizeInMB / 1024;
    return `${sizeInGB.toFixed(2)} GB`;
  };

  const handleFolderClick = (folder: DesignFolder) => {
    navigate(`/utility-systems/design-documentation-integration/folder/${folder.id}`, {
      state: { selectedRegion, selectedPremises, folder },
    });
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      setResMessage("Folder name cannot be empty");
      return;
    }

    setResMessage("");
    setIsCreateFolderModalOpen(false);
    setLoading(true);

    try {
      const data = {
        folderName: folderName.trim(),
        description: folderDescription.trim() || undefined,
        createdDate: new Date().toISOString(),
      };

      const response = await axiosInstance.post("/api/utility-systems/design-folders", data);
      
      if (response.data.success && response.data.resCode === 200) {
        toast.success(response.data.resMessage || "Folder created successfully");
        setFolderName("");
        setFolderDescription("");
        // Refresh folders list immediately to show new folder in table
        await fetchFolders();
      } else {
        setResMessage(response.data.resMessage || "Failed to create folder");
        setIsCreateFolderModalOpen(true);
      }
    } catch (error: any) {
      console.error("Error creating folder:", error);
      const errorMessage = error.response?.data?.resMessage || error.response?.data?.message || "Failed to create folder";
      setResMessage(errorMessage);
      setIsCreateFolderModalOpen(true);
      
      // Show toast for 409 conflict (duplicate name)
      if (error.response?.status === 409) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditFolder = async () => {
    if (!editFolder || !folderName.trim()) {
      setResMessage("Folder name cannot be empty");
      return;
    }

    setResMessage("");
    setIsEditFolderModalOpen(false);
    setLoading(true);

    try {
      const data = {
        folderName: folderName.trim(),
        description: folderDescription.trim() || undefined,
      };

      const id = editFolder._id || editFolder.id;
      const response = await axiosInstance.put(`/api/utility-systems/design-folders/${id}`, data);
      
      if (response.data.success && response.data.resCode === 200) {
        toast.success(response.data.resMessage || "Folder updated successfully");
        setEditFolder(null);
        setFolderName("");
        setFolderDescription("");
        fetchFolders();
      } else {
        setResMessage(response.data.resMessage || "Failed to update folder");
        setIsEditFolderModalOpen(true);
      }
    } catch (error: any) {
      console.error("Error updating folder:", error);
      const errorMessage = error.response?.data?.resMessage || error.response?.data?.message || "Failed to update folder";
      setResMessage(errorMessage);
      setIsEditFolderModalOpen(true);
      
      if (error.response?.status === 409) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folder: DesignFolder) => {
    if (!window.confirm(`Are you sure you want to delete "${folder.folderName}"? This will delete all documents in this folder.`)) {
      return;
    }

    try {
      const id = folder._id || folder.id;
      const response = await axiosInstance.delete(`/api/utility-systems/design-folders/${id}`);
      
      if (response.data.success && response.data.resCode === 200) {
        toast.success(response.data.resMessage || "Folder deleted successfully");
        fetchFolders();
      } else {
        toast.error(response.data.resMessage || "Failed to delete folder");
      }
    } catch (error: any) {
      console.error("Error deleting folder:", error);
      toast.error(error.response?.data?.resMessage || error.response?.data?.message || "Failed to delete folder");
    }
  };

  const handleDownloadFolder = async (folder: DesignFolder) => {
    try {
      setLoading(true);
      const id = folder._id || folder.id;
      const response = await axiosInstance.get(
        `/api/utility-systems/design-folders/${id}/download`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${folder.folderName}.zip`);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Folder download started");
    } catch (error: any) {
      console.error("Error downloading folder:", error);
      const errorMessage = error.response?.data?.resMessage || error.response?.data?.message || "Failed to download folder";
      toast.error(errorMessage);
      
      // Handle empty folder case
      if (error.response?.status === 400) {
        toast.error("Cannot download empty folder");
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (folder: DesignFolder) => {
    setEditFolder(folder);
    setFolderName(folder.folderName);
    setFolderDescription(folder.description || "");
    setIsEditFolderModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateFolderModalOpen(false);
    setFolderName("");
    setFolderDescription("");
    setResMessage("");
  };

  const closeEditModal = () => {
    setIsEditFolderModalOpen(false);
    setEditFolder(null);
    setFolderName("");
    setFolderDescription("");
    setResMessage("");
  };

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold text-gray-900">
            Design & Documentation Integration
          </h1>
          <button
            onClick={() => setIsCreateFolderModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Folder
          </button>
        </div>
        <p className="text-gray-600">
          Organize architectural drawings, blueprints, and permits in folders with version control.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search folders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>

          {/* Region Filter */}
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>

          {/* Premises Filter */}
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>

          {/* Document Type Filter */}
          <div className="min-w-[180px]">
            <select
              value={documentTypeFilter}
              onChange={(e) => setDocumentTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="architectural_drawing">Architectural Drawings</option>
              <option value="blueprint">Blueprints</option>
              <option value="permit">Permits</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {resMessage && (
        <div className={`mb-4 p-3 rounded-lg ${
          resMessage.includes("already exists") || resMessage.includes("cannot be empty")
            ? "bg-red-50 text-red-700"
            : "bg-blue-50 text-blue-700"
        }`}>
          {resMessage}
        </div>
      )}

      {/* Folders List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : folders.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <Folder className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No folders found</p>
            <p className="text-sm">
              {searchQuery || selectedRegion !== "ALL Regions" || selectedPremises !== "ALL Premises"
                ? "Try adjusting your search or filters"
                : "Create your first folder to organize documents"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700">Folder Name</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Description</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">No. of Files</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Folder Size</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {folders.map((folder) => (
                  <tr key={folder.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Folder className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">{folder.folderName}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {folder.description || "-"}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {folder.files?.length || 0} File(s)
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {folder.totalsize || "0 MB"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleFolderClick(folder)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openEditModal(folder)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadFolder(folder)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteFolder(folder)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      <Modal
        isOpen={isCreateFolderModalOpen}
        onClose={closeCreateModal}
        modalClass="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full relative"
      >
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Create Folder</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Folder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setResMessage("");
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter folder name"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={folderDescription}
              onChange={(e) => setFolderDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter folder description (optional)"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={closeCreateModal}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateFolder}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create
          </button>
        </div>
      </Modal>

      {/* Edit Folder Modal */}
      <Modal
        isOpen={isEditFolderModalOpen}
        onClose={closeEditModal}
        modalClass="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full relative"
      >
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Edit Folder</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Folder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setResMessage("");
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter folder name"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={folderDescription}
              onChange={(e) => setFolderDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter folder description (optional)"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={closeEditModal}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleEditFolder}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default DesignDocumentationIntegration;
